import React, {
  useEffect,
  useState,
} from 'react';

import {
  getRecentlyReleasedMovies,
} from '../services/tmdbService';

import SectionHeader from './SectionHeader';
import MediaGridCard from './MediaGridCard';
import Pagination from './Pagination';
import LoadingSpinner from './LoadingSpinner';

import '../styles/main.css';

const Movies = () => {
  /* ============================================================
     STATE
     ============================================================ */

  const [movies, setMovies] = useState([]);

  const [currentPage, setCurrentPage] =
    useState(1);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState(false);

  const [reload, setReload] =
    useState(0);

  /* ============================================================
     FETCH MOVIES
     ============================================================ */

  useEffect(() => {
    let isMounted = true;

    const fetchMovies = async () => {
      setLoading(true);
      setError(false);

      try {
        const results =
          await getRecentlyReleasedMovies(
            currentPage
          );

        if (isMounted) {
          setMovies(
            Array.isArray(results)
              ? results
              : []
          );
        }
      } catch (err) {
        console.error(
          'Error loading movies:',
          err
        );

        if (isMounted) {
          setMovies([]);
          setError(true);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchMovies();

    return () => {
      isMounted = false;
    };
  }, [currentPage, reload]);

  /* ============================================================
     PAGE CHANGE
     ============================================================ */

  const handlePageChange = (
    direction
  ) => {
    setCurrentPage((previousPage) => {
      if (direction === 'next') {
        return previousPage + 1;
      }

      return Math.max(
        previousPage - 1,
        1
      );
    });

    /*
     * Scroll kembali ke bagian atas
     * setelah mengganti halaman.
     */

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  /* ============================================================
     RETRY
     ============================================================ */

  const handleRetry = () => {
    setReload(
      (previous) => previous + 1
    );
  };

  /* ============================================================
     RENDER
     ============================================================ */

  return (
    <section
      className="content-section"
      id="movies"
    >
      {/* ======================================================
          NOTICE
          ====================================================== */}

      <div className="section-notice">
        <i className="fas fa-triangle-exclamation"></i>{' '}

        We do not host, store, or transmit any
        movies, TV shows, or other media files
        on our own infrastructure. The service
        helps you discover and access content
        through third-party sources. We do not
        control those sources or their content.

        <br />

        Please disable your ad blocker. We
        do not show ads while you watch.

        <br />

        <b
          style={{
            color: '#ac77ff',
          }}
        >
          An account is NOT required to access
          and use Defilms.
        </b>
      </div>

      {/* ======================================================
          SECTION HEADER
          ====================================================== */}

      <SectionHeader
        title="Movies"
        icon="fa-solid fa-film"
      >
        <div className="movies-sort-label">
          <i className="fa-solid fa-clock"></i>

          <span>
            Latest Theatrical Releases
          </span>
        </div>
      </SectionHeader>

      {/* ======================================================
          LOADING
          ====================================================== */}

      {loading && (
        <LoadingSpinner
          label="Loading latest movies"
        />
      )}

      {/* ======================================================
          ERROR
          ====================================================== */}

      {!loading && error && (
        <div className="movies-message">
          <i className="fa-solid fa-circle-exclamation"></i>

          <p>
            Unable to load movies.
          </p>

          <button
            type="button"
            onClick={handleRetry}
          >
            Try Again
          </button>
        </div>
      )}

      {/* ======================================================
          EMPTY
          ====================================================== */}

      {!loading &&
        !error &&
        movies.length === 0 && (
          <div className="movies-message">
            <i className="fa-solid fa-film"></i>

            <p>
              No movies found.
            </p>
          </div>
        )}

      {/* ======================================================
          MOVIE GRID
          ====================================================== */}

      {!loading &&
        !error &&
        movies.length > 0 && (
          <div className="media-grid">
            {movies.map((movie) => (
              <MediaGridCard
                key={movie.id}
                item={movie}
                linkTo={`/movie/${movie.id}`}
              />
            ))}
          </div>
        )}

      {/* ======================================================
          PAGINATION
          ====================================================== */}

      {!error && (
        <Pagination
          currentPage={currentPage}
          onPageChange={
            handlePageChange
          }
        />
      )}
    </section>
  );
};

export default Movies;